import { cn } from "~/lib/utils";
import { DataGridCell } from "./DataGridCell";
import { ColumnMetadata, RowSize, RowSizes } from "./types";
import React, { type JSX } from "react";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { useUser } from "~/modules/user/hooks/useUser";

// // // //

interface Props<RowType, ColumnTypes> {
    row: RowType;
    columns: ColumnMetadata<RowType, ColumnTypes>[];
    className?: string;
    size?: RowSize;
    striped?: boolean;
    scrollLeft?: number;
    bordered?: boolean;
    tabIndex?: number;
    onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLTableRowElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLTableRowElement>) => void;
    onMouseEnter?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
    onMouseLeave?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
}

function DataGridRowContent<RowType, ColumnTypes>(
    props: Props<RowType, ColumnTypes>,
) {
    const {
        columns,
        row,
        className = "",
        size = RowSizes.medium,
        bordered = false,
        striped = false,
        scrollLeft = 0,
        tabIndex,
        onClick,
        onFocus,
        onBlur,
        onMouseEnter,
        onMouseLeave,
    } = props;

    const { isLoggedIn, isPaidPlan } = useUser();

    return (
        <tr
            className={cn("flex flex-row group/row", {
                [className]: className !== "",
                "bg-grid-row": className === "" && !striped,
                "bg-grid-row-striped": className === "" && striped,
                "divide-x": bordered,
            })}
            onFocus={onFocus}
            onBlur={onBlur}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            {...{
                onClick,
                role: onClick !== undefined ? "button" : undefined,
            }}
        >
            {columns.map((c) => (
                <FeatureFlag
                    feature={c.featureFlag?.feature || undefined}
                    invert={c.featureFlag?.invert}
                    key={c.slug}
                >
                    <DataGridCell
                        row={row}
                        column={c}
                        size={size}
                        isLoggedIn={isLoggedIn}
                        hasProPlan={isPaidPlan}
                        addBoxShadow={
                            c.sticky !== undefined &&
                            c.sticky.isLast &&
                            scrollLeft !== 0
                        }
                    />
                </FeatureFlag>
            ))}
        </tr>
    );
}

export const DataGridRow = React.memo(DataGridRowContent) as <
    RowType,
    ColumnTypes,
>(
    props: Props<RowType, ColumnTypes>,
) => JSX.Element;
